/**
 * Sessions Store
 * Manages active and recent sessions with polling
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import { ActiveSession, RecentSession } from '../types/dashboard-types';

// Polling interval for active sessions (30 seconds)
const ACTIVE_SESSIONS_POLL_INTERVAL = 30000;

interface SessionsState {
  // Active sessions
  activeSessions: ActiveSession[];
  totalActiveCount: number;
  totalActiveEnergy: number;
  totalActiveRevenue: number;

  // Recent sessions
  recentSessions: RecentSession[];
  recentTotalCount: number;
  recentTotalPages: number;
  recentCurrentPage: number;
  recentPageSize: number;
  recentHours: number;

  // Loading states
  isLoadingActive: boolean;
  isLoadingRecent: boolean;

  // Error states
  activeError: string | null;
  recentError: string | null;

  // Polling state
  activePollIntervalId: ReturnType<typeof setInterval> | null;
  lastActiveUpdate: Date | null;

  // Actions
  loadActiveSessions: () => Promise<void>;
  loadRecentSessions: () => Promise<void>;
  loadRecentPage: (page: number) => Promise<void>;
  setRecentPageSize: (size: number) => void;
  setRecentHours: (hours: number) => void;
  startPolling: () => void;
  stopPolling: () => void;
  reset: () => void;
}

const initialState = {
  activeSessions: [],
  totalActiveCount: 0,
  totalActiveEnergy: 0,
  totalActiveRevenue: 0,
  recentSessions: [],
  recentTotalCount: 0,
  recentTotalPages: 0,
  recentCurrentPage: 0,
  recentPageSize: 25,
  recentHours: 24,
  isLoadingActive: false,
  isLoadingRecent: false,
  activeError: null,
  recentError: null,
  activePollIntervalId: null,
  lastActiveUpdate: null,
};

export const useSessionsStore = create<SessionsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      loadActiveSessions: async () => {
        set({ isLoadingActive: true, activeError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getActiveSessionsUseCase.execute();

          if (result.hasError) {
            set({
              activeError: result.errorMessage,
              isLoadingActive: false,
            });
            return;
          }

          set({
            activeSessions: result.sessions,
            totalActiveCount: result.totalCount,
            totalActiveEnergy: result.totalEnergy,
            totalActiveRevenue: result.totalRevenue,
            isLoadingActive: false,
            lastActiveUpdate: new Date(),
          });
        } catch (error) {
          set({
            activeError: error instanceof Error ? error.message : 'Failed to load active sessions',
            isLoadingActive: false,
          });
        }
      },

      loadRecentSessions: async () => {
        const { recentCurrentPage, recentPageSize, recentHours } = get();
        set({ isLoadingRecent: true, recentError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getRecentSessionsUseCase.execute(
            recentCurrentPage,
            recentPageSize,
            recentHours
          );

          if (result.hasError) {
            set({
              recentError: result.errorMessage,
              isLoadingRecent: false,
            });
            return;
          }

          set({
            recentSessions: result.sessions,
            recentTotalCount: result.totalCount,
            recentTotalPages: result.totalPages,
            isLoadingRecent: false,
          });
        } catch (error) {
          set({
            recentError: error instanceof Error ? error.message : 'Failed to load recent sessions',
            isLoadingRecent: false,
          });
        }
      },

      loadRecentPage: async (page: number) => {
        set({ recentCurrentPage: page });
        await get().loadRecentSessions();
      },

      setRecentPageSize: (size: number) => {
        set({ recentPageSize: size, recentCurrentPage: 0 });
        get().loadRecentSessions();
      },

      setRecentHours: (hours: number) => {
        set({ recentHours: hours, recentCurrentPage: 0 });
        get().loadRecentSessions();
      },

      startPolling: () => {
        const { activePollIntervalId } = get();

        // Clear existing interval
        if (activePollIntervalId) clearInterval(activePollIntervalId);

        // Start new polling interval
        const newInterval = setInterval(() => {
          get().loadActiveSessions();
        }, ACTIVE_SESSIONS_POLL_INTERVAL);

        set({ activePollIntervalId: newInterval });
      },

      stopPolling: () => {
        const { activePollIntervalId } = get();
        if (activePollIntervalId) clearInterval(activePollIntervalId);
        set({ activePollIntervalId: null });
      },

      reset: () => {
        get().stopPolling();
        set(initialState);
      },
    }),
    {
      name: 'sessions-storage',
      partialize: (state) => ({
        recentPageSize: state.recentPageSize,
        recentHours: state.recentHours,
      }),
    }
  )
);
